<script lang="ts" setup>
  import { computed, onMounted, ref, toRef, toRefs } from 'vue'

  import type { EffortJsonldVueGlobalSearchDefaultReadSync } from '@/api'
  import { getIdFromIri, isIri } from '@/helpers/iri'
  import { InternalLinkIcon } from '@/config/icons'
  import { useGeneratedEffortStore } from '@/stores'
  import { RouterLink } from 'vue-router'

  interface Props {
    id: number | EffortJsonldVueGlobalSearchDefaultReadSync | string | undefined
    target?: '_blank'
    fetchTitle?: boolean
    noIcon?: boolean
    bordered?: boolean
    onPreClick?: (event: Event, effortId: number) => void
    showId?: boolean
  }

  interface PreClickBag {
    cancel?: boolean
  }

  const props = withDefaults(defineProps<Props>(), {
    fetchTitle: false,
    noIcon: false,
    target: undefined,
    bordered: false,
    showId: false,
    onPreClick: () => {
      /*NoOp*/
    }
  })
  const { target, fetchTitle, noIcon } = toRefs(props)
  const idOrObj = toRef<number | EffortJsonldVueGlobalSearchDefaultReadSync | string | undefined>(props.id)
  const numericId = computed<number | undefined>(() => {
    if (typeof idOrObj.value == 'undefined') return undefined
    else if (typeof idOrObj.value === 'number') return idOrObj.value
    else if (typeof idOrObj.value === 'string') {
      if (isIri(idOrObj.value)) return Number(getIdFromIri(idOrObj.value))
      else if (!isNaN(Number(idOrObj.value))) return Number(idOrObj.value)
      return undefined
    } else if (idOrObj.value.id) return idOrObj.value.id
    return undefined
  })

  const fetchedTitle = ref<string>()
  onMounted(async () => {
    if (numericId.value && fetchTitle.value) {
      const effort = await useGeneratedEffortStore()
        .fetchById(numericId.value.toString())
        .then((d) => d.value)
      fetchedTitle.value = effort?.title ?? undefined
    }
  })

  const onClick = (event: Event) => {
    props.onPreClick?.(event, numericId.value as number)
  }
</script>

<template>
  <RouterLink
    v-if="numericId"
    :active-class="''"
    :target="target"
    :class="[
      'inline items-center px-0.5',
      {
        'hover:bg-[var(--p-button-primary-border-color)] rounded': bordered
      }
    ]"
    :exact-active-class="''"
    :to="{ name: 'effort_details', params: { id: numericId } }"
    @click="onClick"
    v-bind="$attrs">
    <div :class="['flex-1 inline gap-x-1 items-center leading-normal text-blue-700 font-semibold px-0.5', { 'hover:text-[var(--p-button-primary-color)]': bordered, 'inline-flex': !noIcon }]">
      <span v-if="$titrop.showIds || showId" :class="noIcon ? 'mr-1' : ''">[{{ numericId }}]</span>
      <slot v-if="!fetchTitle" />
      <span v-else>
        {{ fetchedTitle }}
      </span>
      <InternalLinkIcon
        v-if="!noIcon"
        aria-hidden="true"
        class="h-4 w-4" />
    </div>
    <slot
      :effort-id="numericId"
      class="flex-0 self-end"
      name="append" />
  </RouterLink>

  <slot
    v-else
    v-bind="$attrs" />
</template>
